import { IAddCartResult } from "../Product/Interfaces/IAddCartResult.js";
import { IEmarsys } from "../Product/Interfaces/IEmarsys.js";
import { ILinkedProductModel } from "../Product/Interfaces/ILinkedProductModel.js";

declare const ScarabQueue: IEmarsys;

export class BasketCommon {
    private localeUrlPrefix = document.body.dataset.localeUrlPrefix;

    public AddToBasket(productId: number, price: number): void {
        let postData = { productId: productId, price: null as number | null };

        if (price) { // For gift cards only, the customer sets the price.
            postData.price = price;
        }

        fetch(this.localeUrlPrefix + "/ws/add-item",
            {
                body: JSON.stringify(postData),
                headers: { "Content-Type": "application/json" },
                method: "POST",
            })
            .then(response => {
                if (response?.ok || response?.status === 400) {
                    // 400 errors will contain error messages in the response body.
                    return response.json();
                }
                throw new Error("Add to basket failed.");
            })
            .then((data: IAddCartResult) => {
                if (data.errors && data.errors.length > 0) {
                    this.addTocartFailed(data);
                }
                else {
                    this.basketSuccess(data);
                }
            })
            .catch((error: Error) => {
                window.IssueLogger.log(error.message, "addToBasket", error);
                this.addTocartFailed();
            });
    }


    public WireUpPriceEditMonitoring(onGiftCardPriceChanged: () => void): void {
        // Catch enter, filter non numeric, on key press, if less than 5 or not numeric, disable button.
        let giftVoucherPriceElement = document.getElementById("gift-voucher-price");

        if (giftVoucherPriceElement) {
            giftVoucherPriceElement.addEventListener("keydown", (e) => this.filterGiftCardInput(e));
            giftVoucherPriceElement.addEventListener("keyup", () => onGiftCardPriceChanged());
        }
    }

    public giftCardValue(): number | null {
        let priceElement = document.getElementById('gift-voucher-price') as HTMLInputElement;
        let priceAsText = priceElement.value;

        if (isNaN(parseFloat(priceAsText)) || !isFinite(parseFloat(priceAsText))) {
            return null;
        }

        return parseInt(priceAsText, 10);
    }

    private filterGiftCardInput(e: KeyboardEvent): void {
        if (e.key === "Enter" || e.key === "Delete" || e.key === ".") {
            e.preventDefault();
        }
    }

    private basketSuccess(cartResult: IAddCartResult) {
        let dialog = document.getElementById("PopupDialog") as HTMLDivElement;
        let itemCountElement = document.querySelector(".item-count");

        // Add active to the parent div as it won't have it if this is the first item being added dynamically.
        itemCountElement.parentElement.classList.add("active");
        itemCountElement.textContent = cartResult.itemCount.toString();
        dialog.querySelector(".QuickAdd_Name").textContent = cartResult.mostRecentProductName;

        if (cartResult.colour) {
            dialog.querySelector(".QuickAdd_Colour").textContent = "Colour: " + cartResult.colour;
        }
        else {
            dialog.querySelector(".QuickAdd_Colour").textContent = "";
        }

        if (cartResult.size) {
            dialog.querySelector(".QuickAdd_Size").textContent = "Size: " + cartResult.size;
        }
        else {
            dialog.querySelector(".QuickAdd_Size").textContent = "";
        }

        dialog.querySelector(".QuickAdd_Image").setAttribute("src", cartResult.imageUrl);

        const bvRatingElement = dialog.querySelector<HTMLDivElement>(".bazaarvoice");

        if (bvRatingElement) {
            // Use setAttr as JS will interpret casing as a dash char.
            bvRatingElement.dataset.bvProductid = cartResult.styleColourCode;
            bvRatingElement.dataset.bvRedirectUrl = cartResult.styleUrl;
            bvRatingElement.dataset.bvShow = "inline_rating";
        }

        dialog.querySelector(".item-price").textContent = cartResult.mostRecentProductPrice;

        let quickAddInner = dialog.querySelector(".linked-results") as HTMLDivElement;

        if (cartResult.linkedProducts.length === 0) {
            quickAddInner.classList.add("hidden");
        }
        else {
            quickAddInner.classList.remove("hidden");
        }
        quickAddInner.innerHTML = "<h3>Would you also like to add these recommended accessories?</h3>";

        for (let i = 0; i < cartResult.linkedProducts.length; i++) {
            let html = this.createLinkedProductHtml(cartResult.linkedProducts[i]);

            quickAddInner.insertAdjacentElement("beforeend", html);
        }

        window.Modal.OpenModal(dialog, () => { });

        this.pushToEmarsys(cartResult);
        this.pushToGoogle(cartResult);
    }

    private pushToEmarsys(cartResult: IAddCartResult): void {

        if (cartResult.errors && cartResult.errors.length > 0) {
            return;
        }

        ScarabQueue.push(['cart', [
            { item: cartResult.styleCode, price: cartResult.priceGbp, quantity: 1 }
        ]]);

        ScarabQueue.push(['go']);
    }

    private pushToGoogle(cartResult: IAddCartResult): void {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            "event": "add_to_cart",
            "ecommerce": {
                "currency": document.body.dataset.currency,
                "items": [
                    {
                        item_id: cartResult.productId,
                        item_poscode: cartResult.posCode,
                        item_stylecolour: cartResult.styleColourCode,
                        item_name: cartResult.mostRecentProductName,
                        item_brand: cartResult.brandName,
                        item_category: cartResult.categoryLevel1,
                        item_category2: cartResult.categoryLevel2,
                        item_category3: cartResult.categoryLevel3,
                        item_category4: cartResult.categoryLevel4,
                        "price": cartResult.price,
                        "quantity": 1
                    }
                ]
            }
        });
    }

    private createLinkedProductHtml(product: ILinkedProductModel): HTMLDivElement {
        const para = document.createElement("p");
        para.classList.add("QuickAdd_Info");

        const productNameLink = document.createElement("a");
        productNameLink.setAttribute("href", product.styleUrl);
        productNameLink.classList.add("QuickAdd_Name");
        productNameLink.textContent = product.name;
        para.appendChild(productNameLink);

        const descriptionSpan = document.createElement("span");
        descriptionSpan.textContent = product.description;
        para.appendChild(descriptionSpan);

        const spanPrice = document.createElement("span");
        spanPrice.classList.add("QuickAdd_Price");
        spanPrice.textContent = product.formattedPrice;

        const control = document.createElement("div");
        control.classList.add("control-box");

        const basketAddLink = document.createElement("a");
        basketAddLink.classList.add("btn");
        basketAddLink.textContent = "Yes please";
        basketAddLink.addEventListener("click", (e) => {
            e.preventDefault();
            this.AddToBasket(product.productId, null);
        });

        control.appendChild(spanPrice);
        control.appendChild(basketAddLink);

        const relatedImage = document.createElement("img");
        relatedImage.setAttribute("src", product.imageUrl);
        relatedImage.setAttribute("height", "64");
        relatedImage.setAttribute("width", "64");

        const linkBox = document.createElement("div");
        linkBox.classList.add("linked-result");
        linkBox.appendChild(relatedImage);
        linkBox.appendChild(para);
        linkBox.appendChild(control);

        return linkBox;
    }

    private addTocartFailed(data?: IAddCartResult): void {
        let modal = <HTMLDivElement>document.getElementById("product-add-error-modal");
        let errorList = modal.querySelector<HTMLUListElement>(".error-list");

        if (data?.errors && data.errors.length > 0) {
            errorList.innerHTML = "";

            data.errors.forEach((error) => {
                let li = document.createElement("li");
                li.textContent = error;
                errorList.appendChild(li);
            });
        }
        else {
            errorList.innerHTML = "<li>This might be due to a temporary network problem, please try again soon.</li>";
        }

        window.Modal.OpenModal(modal, () => { });
    }
}
